import {
  ChangeDetectionStrategy,
  Component,
  effect,
  type ElementRef,
  inject,
  isDevMode,
  signal,
  viewChild,
} from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { ShowIfLoggedInDirective } from '../../user/directive/show-if-logged-in.directive';
import { HasAccessDirective } from '../../user/directive/has-access.directive';
import { ShowOnDesktopDirective } from '../../../core/device/show-on-desktop.directive';
import { ShowOnMobileOrTabletDirective } from '../../../core/device/show-on-mobile-or-tablet.directive';
import { PushNotificationService } from '../../../core/pwa/push-notification/push-notification.service';
import type { OverlayRef } from '@angular/cdk/overlay';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { NotificationComponent } from '../notification/notification.component';
import { UserStateSelectors } from '../../user/state/user-state-selectors';
import { select } from '@ngxs/store';

type SubMenu = 'home' | 'admin';

@Component({
  selector: 'app-header',
  imports: [
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    RouterLink,
    ShowIfLoggedInDirective,
    HasAccessDirective,
    ShowOnDesktopDirective,
    ShowOnMobileOrTabletDirective,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  readonly #pushNotificationService!: PushNotificationService;
  readonly #overlay = inject(Overlay);
  #overlayRef: OverlayRef | null = null;

  protected isActive = false;
  protected openedMobileSubmenu: SubMenu | null = null;
  protected readonly focusResetTarget = viewChild<ElementRef<HTMLDivElement>>('focusResetTarget');
  protected readonly showNotification = signal(false); //mock true
  protected readonly isAdmin = select(UserStateSelectors.isAdmin);

  constructor() {
    if (!isDevMode()) {
      this.#pushNotificationService = inject(PushNotificationService);
      effect(() => {
        this.showNotification.set(this.#pushNotificationService.status() === 'SUBSCRIBED');
      });
    }
  }

  protected toggleMobileMenu(): void {
    this.isActive = !this.isActive;
  }

  protected changeMobileSubmenuState(submenu: SubMenu, $event: MouseEvent): void {
    this.openedMobileSubmenu = submenu === this.openedMobileSubmenu ? null : submenu;
    $event.preventDefault();
  }

  protected onSubmenuItemClick(): void {
    this.focusResetTarget()?.nativeElement.focus();
  }

  protected onNotificationClick(): void {
    if (this.#overlayRef !== null) {
      return;
    }

    const positionStrategy = this.#overlay.position().global().top('0px').right('0px');

    this.#overlayRef = this.#overlay.create({
      positionStrategy,
      hasBackdrop: true,
      panelClass: ['z-[1000]', 'overflow-auto', 'w-full', 'sm:w-auto'],
      disposeOnNavigation: false,
    });

    const portal = new ComponentPortal(NotificationComponent, null);
    const componentRef = this.#overlayRef.attach(portal);

    this.#overlayRef.backdropClick().subscribe(() => {
      this.#disposeOverlay();
    });

    componentRef.instance.closeElement.subscribe(() => {
      this.#disposeOverlay();
    });
  }

  #disposeOverlay(): void {
    this.#overlayRef?.dispose();
    this.#overlayRef = null;
  }
}
